import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Swipe-down-to-dismiss for mobile bottom sheets
const useSwipeToClose = (el, closeRef) => {
  React.useEffect(() => {
    if (!el) return
    const drag = { y0: 0, dy: 0, active: false, t0: 0 }

    const isMobile = () => window.innerWidth < 640

    const onTouchStart = (e) => {
      if (!isMobile()) return
      drag.y0 = e.touches[0].clientY
      drag.dy = 0
      drag.active = false
      drag.t0 = Date.now()
    }

    const onTouchMove = (e) => {
      if (!isMobile()) return
      const dy = e.touches[0].clientY - drag.y0
      if (!drag.active) {
        if (dy > 10 && el.scrollTop <= 0) {
          drag.active = true
        } else {
          return
        }
      }
      if (e.cancelable) e.preventDefault()
      drag.dy = Math.max(0, dy)
      el.style.transition = "none"
      el.style.transform = `translateX(-50%) translateY(${drag.dy}px)`
    }

    const onTouchEnd = () => {
      if (!drag.active) return
      const velocity = drag.dy / Math.max(1, Date.now() - drag.t0)
      if (drag.dy > 120 || velocity > 0.55) {
        el.style.transition = "transform 0.22s cubic-bezier(0.4, 0, 1, 1), opacity 0.22s ease"
        el.style.transform = "translateX(-50%) translateY(105%)"
        el.style.opacity = "0.4"
        setTimeout(() => closeRef.current?.click(), 160)
      } else {
        el.style.transition = "transform 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.15)"
        el.style.transform = "translateX(-50%) translateY(0px)"
        setTimeout(() => {
          el.style.transition = ""
          el.style.transform = ""
        }, 360)
      }
      drag.active = false
    }

    el.addEventListener("touchstart", onTouchStart, { passive: true })
    el.addEventListener("touchmove", onTouchMove, { passive: false })
    el.addEventListener("touchend", onTouchEnd, { passive: true })
    return () => {
      el.removeEventListener("touchstart", onTouchStart)
      el.removeEventListener("touchmove", onTouchMove)
      el.removeEventListener("touchend", onTouchEnd)
    }
  }, [el, closeRef])
}

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const [contentNode, setContentNode] = React.useState(null)
  const closeRef = React.useRef(null)
  useSwipeToClose(contentNode, closeRef)

  const setRefs = React.useCallback((node) => {
    setContentNode(node)
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRefs}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[92dvh] overflow-y-auto overscroll-contain max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-t-2xl max-sm:border-b-0 max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-sm:data-[state=open]:slide-in-from-bottom-8 max-sm:data-[state=closed]:slide-out-to-bottom-8",
          className
        )}
        {...props}>
        <div
          className="sm:hidden sticky top-0 z-10 -mt-3 -mb-3 flex justify-center pt-1 pb-2 touch-none"
          data-testid="sheet-drag-handle"
        >
          <div className="h-1.5 w-12 rounded-full bg-white/25" />
        </div>
        {children}
        <DialogPrimitive.Close
          ref={closeRef}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
